import type { FunctionComponent, MouseEventHandler } from 'react';
import { useCallback } from 'react';

import { ComponentWrapper } from '@trello/component-wrapper';
import { Button } from '@trello/nachos/button';
import { HelpIcon } from '@trello/nachos/icons/help';
import type { UpgradePromptTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import {
  LazyUpgradeSmartComponent,
  useUpgradePromptRules,
} from 'app/src/components/UpgradePrompts';
import { useInteractiveTooltip } from './useInteractiveTooltip';

import styles from './WorkspaceMemberRestrictedPermissionButton.less';

export const WorkspaceMemberRestrictedPermissionButtonUnwrapped: FunctionComponent<{
  orgBillingUrl: string;
  orgMemberType: string;
  orgId?: string;
}> = ({ orgMemberType, orgId }) => {
  const { toggleTooltip, hideTooltip, InteractiveTooltip } =
    useInteractiveTooltip();

  const { shouldDisplayUpgradePrompt } = useUpgradePromptRules(orgId, '');

  const onMouseLeave: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (e.relatedTarget && e.relatedTarget !== window) {
        hideTooltip();
      }
    },
    [hideTooltip],
  );

  const onClick = useCallback(() => {
    if (shouldDisplayUpgradePrompt) {
      toggleTooltip();
    }
  }, [shouldDisplayUpgradePrompt, toggleTooltip]);

  const iconAfter = shouldDisplayUpgradePrompt ? (
    <HelpIcon size="small" color={token('color.icon.subtle', '#626F86')} />
  ) : undefined;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={styles.wrapper} onMouseLeave={onMouseLeave}>
      <Button
        onClick={onClick}
        iconAfter={iconAfter}
        className={styles.button}
        testId={getTestId<UpgradePromptTestIds>(
          'org-member-restricted-permission',
        )}
      >
        {orgMemberType}
      </Button>
      <InteractiveTooltip>
        <LazyUpgradeSmartComponent
          orgId={orgId || ''}
          promptId="orgMemberRestrictedPromptFull"
          allowUpsell
        />
      </InteractiveTooltip>
    </div>
  );
};

export const WorkspaceMemberRestrictedPermissionButton: FunctionComponent<{
  orgBillingUrl: string;
  orgMemberType: string;
  orgId?: string;
}> = (props) => (
  <ComponentWrapper>
    <WorkspaceMemberRestrictedPermissionButtonUnwrapped {...props} />
  </ComponentWrapper>
);
